import React from 'react';
import { BEV_FINISHED } from '../../bird-eye/mockData';
import type { BirdEyeBoardState, BirdEyeDragSource, BirdEyeDropTarget, BirdEyeFinishedSummary } from '../../bird-eye/boardTypes';
import { formatMoney } from '../../bird-eye/utils';
import { Button } from '../Button/Button';
import { BirdEyeDndProvider } from './BirdEyeDndContext';
import { BirdEyeLane } from './BirdEyeLane';
import { BirdEyeOperatoryBox } from './BirdEyeOperatoryBox';
import { BirdEyePatientCard } from './BirdEyePatientCard';

export interface BirdEyeBoardProps {
  state: BirdEyeBoardState;
  onMove: (from: BirdEyeDragSource, to: BirdEyeDropTarget) => void;
  onViewFinished?: () => void;
  finishedSummary?: BirdEyeFinishedSummary;
}

export function BirdEyeBoard({ state, onMove, onViewFinished, finishedSummary }: BirdEyeBoardProps) {
  const seated = state.ops.filter((o) => o.seated).length;
  const finished = finishedSummary ?? { total: BEV_FINISHED.total, production: BEV_FINISHED.production };

  return (
    <BirdEyeDndProvider onMove={onMove}>
      <div className="grid min-h-0 flex-1 grid-cols-5 grid-rows-[minmax(0,1fr)] gap-4 pb-5">
        <BirdEyeLane
          icon="phone-in"
          title="Arriving soon"
          accent="var(--ai-4)"
          count={state.arriving.length}
          dropTarget={{ type: 'list', lane: 'arriving' }}
        >
          {state.arriving.map((p) => (
            <BirdEyePatientCard
              key={p.id}
              patient={p}
              dragSource={{ type: 'list', lane: 'arriving', patientId: p.id }}
            />
          ))}
        </BirdEyeLane>
        <BirdEyeLane
          icon="clock"
          title="Waiting room"
          accent="var(--gold)"
          count={state.waiting.length}
          sub="avg 22m"
          dropTarget={{ type: 'list', lane: 'waiting' }}
        >
          {state.waiting.map((p) => (
            <BirdEyePatientCard
              key={p.id}
              patient={p}
              dragSource={{ type: 'list', lane: 'waiting', patientId: p.id }}
            />
          ))}
        </BirdEyeLane>
        <BirdEyeLane
          icon="pulse"
          title="In operatory"
          accent="var(--ai-3)"
          count={`${String(seated)}/${String(state.ops.length)}`}
        >
          {state.ops.map((op, i) => (
            <BirdEyeOperatoryBox key={op.room} operatory={op} opIndex={i} />
          ))}
        </BirdEyeLane>
        <BirdEyeLane
          icon="arrow-r"
          title="Walking out"
          accent="var(--ai-5)"
          count={state.walking.length}
          dropTarget={{ type: 'list', lane: 'walking' }}
        >
          {state.walking.map((p) => (
            <BirdEyePatientCard
              key={p.id}
              patient={p}
              dragSource={{ type: 'list', lane: 'walking', patientId: p.id }}
            />
          ))}
        </BirdEyeLane>
        <BirdEyeLane icon="check-circ" title="Finished" accent="var(--success)" count={finished.total}>
          <div className="rounded-xl border border-[var(--cream-2)] border-l-[3px] border-l-[var(--success)] bg-[var(--cream-1)] px-3.5 py-4 text-center shadow-[var(--sh-1)]">
            <div className="font-[var(--font-display)] text-[34px] font-bold leading-none tracking-[-0.03em] text-[var(--success)]">
              {finished.total}
            </div>
            <div className="mt-1 text-xs text-[var(--ink-3)]">patients done today</div>
            <div className="mt-3 border-t border-[var(--cream-2)] pt-3 font-[var(--font-mono)] text-[13px] font-bold text-[var(--ink-0)]">
              {formatMoney(finished.production)}
            </div>
            <div className="text-[11px] text-[var(--ink-3)]">production captured</div>
            <div className="mt-3">
              <Button variant="secondary" size="sm" icon="arrow-r" onClick={onViewFinished}>
                View all
              </Button>
            </div>
          </div>
        </BirdEyeLane>
      </div>
    </BirdEyeDndProvider>
  );
}
